import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * ANTHILL split slideshow behavior.
 *
 * Keeps slideshow state inside the component so Shopify section reloads don't
 * leave document-level listeners or ad-hoc properties behind.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement[]} [paginationItems]
 * @property {HTMLButtonElement} [autoplayToggle]
 * @property {HTMLElement} [status]
 *
 * @extends {Component<Refs>}
 */
export class AnthillSplitSlideshowComponent extends Component {
  #index = 0;

  /** @type {number | undefined} */
  #timer = undefined;

  #userPaused = false;
  #editorPaused = false;
  #hoverPaused = false;
  #focusPaused = false;
  #touchStartX = 0;
  #touchStartY = 0;
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true, signal });
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: true, signal });
    this.addEventListener('mouseenter', this.#handleMouseEnter, { signal });
    this.addEventListener('mouseleave', this.#handleMouseLeave, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });

    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect, { signal });

    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();

    if (this.slides.length === 0) {
      this.#stopAutoplay();
      return;
    }

    if (this.#index >= this.slides.length) {
      this.#index = 0;
    }

    if (this.refs.autoplayToggle) {
      this.refs.autoplayToggle.hidden = prefersReducedMotion();
    }

    this.#updateAutoplayToggle();
    this.#show(this.#index, false);
    this.#startAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stopAutoplay();
    this.#stopAllVideos();
    this.#controller?.abort();
    this.#controller = null;
  }

  get slides() {
    return this.refs.slides ?? [];
  }

  get autoplayEnabled() {
    return this.hasAttribute('autoplay');
  }

  get autoplayDelay() {
    const attribute = this.getAttribute('autoplay-delay');
    if (attribute === null) return 6000;

    const value = Number(attribute);
    return Number.isFinite(value) && value > 0 ? value : 6000;
  }

  #initialize() {
    if (this.slides.length === 0) return;

    if (this.refs.autoplayToggle) {
      this.refs.autoplayToggle.hidden = prefersReducedMotion();
    }

    this.#updateAutoplayToggle();
    this.#show(0, false);
    this.#startAutoplay();
  }

  /**
   * Selects the previous slide.
   */
  previous() {
    this.#show(this.#index - 1, true);
    this.#restartAutoplay();
  }

  /**
   * Selects the next slide.
   */
  next() {
    this.#show(this.#index + 1, true);
    this.#restartAutoplay();
  }

  /**
   * Selects the slide associated with a pagination control.
   * @param {Event} event
   */
  select(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const index = event.target.dataset.index;
    if (index === undefined) return;

    const nextIndex = Number(index);
    if (!Number.isInteger(nextIndex)) return;

    this.#show(nextIndex, true);
    this.#restartAutoplay();
  }

  /**
   * Toggles user-controlled autoplay pause state.
   */
  toggleAutoplay() {
    this.#userPaused = !this.#userPaused;
    this.#updateAutoplayToggle();

    if (this.#userPaused) {
      this.#stopAutoplay();
    } else {
      // Explicitly choosing Play overrides interaction-paused state.
      this.#hoverPaused = false;
      this.#focusPaused = false;
      this.#startAutoplay();
    }
  }

  /**
   * @param {number} nextIndex
   * @param {boolean} announce
   */
  #show(nextIndex, announce) {
    const slides = this.slides;
    if (slides.length === 0 || !Number.isInteger(nextIndex)) return;

    this.#index = ((nextIndex % slides.length) + slides.length) % slides.length;
    this.#stopAllVideos();

    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.#index;

      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('inert', !active);
    });

    this.refs.paginationItems?.forEach((item, itemIndex) => {
      const active = itemIndex === this.#index;

      item.classList.toggle('is-active', active);
      if (active) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });

    const activeSlide = slides[this.#index];
    if (!activeSlide) return;

    this.#playActiveVideo(activeSlide);

    if (announce) {
      this.#announceSlide(activeSlide);
    }
  }

  /**
   * @param {HTMLElement} slide
   */
  #announceSlide(slide) {
    const { status } = this.refs;
    const message = slide.dataset.status;

    if (!status || !message) return;

    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  #stopAllVideos() {
    for (const slide of this.slides) {
      for (const element of slide.querySelectorAll('video.anthill-slideshow__video')) {
        if (element instanceof HTMLVideoElement) {
          element.pause();
        }
      }
    }
  }

  /**
   * @param {HTMLElement} slide
   * @returns {HTMLVideoElement[]}
   */
  #getVisibleVideos(slide) {
    /** @type {HTMLVideoElement[]} */
    const videos = [];

    for (const element of slide.querySelectorAll('video.anthill-slideshow__video')) {
      if (element instanceof HTMLVideoElement && element.offsetWidth > 0 && element.offsetHeight > 0) {
        videos.push(element);
      }
    }

    return videos;
  }

  /**
   * @param {HTMLElement} slide
   */
  #playActiveVideo(slide) {
    if (!slide.hasAttribute('data-video-autoplay')) return;

    for (const video of this.#getVisibleVideos(slide)) {
      video.muted = true;
      video.play().catch((error) => {
        if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError')) return;

        console.error('ANTHILL split slideshow video could not play.', error);
      });
    }
  }

  #stopAutoplay() {
    if (this.#timer === undefined) return;

    window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #startAutoplay() {
    this.#stopAutoplay();

    if (
      !this.autoplayEnabled ||
      this.#userPaused ||
      this.#editorPaused ||
      this.#hoverPaused ||
      this.#focusPaused ||
      prefersReducedMotion() ||
      document.hidden ||
      this.slides.length < 2
    ) {
      return;
    }

    this.#timer = window.setInterval(() => {
      this.#show(this.#index + 1, false);
    }, this.autoplayDelay);
  }

  #restartAutoplay() {
    this.#stopAutoplay();
    this.#startAutoplay();
  }

  #updateAutoplayToggle() {
    const toggle = this.refs.autoplayToggle;
    if (!toggle) return;

    toggle.classList.toggle('is-paused', this.#userPaused);

    const label = this.#userPaused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
    if (label) toggle.setAttribute('aria-label', label);
  }

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    const touch = event.changedTouches.item(0);
    if (!touch) return;

    this.#touchStartX = touch.screenX;
    this.#touchStartY = touch.screenY;
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    if (this.slides.length < 2) return;

    const touch = event.changedTouches.item(0);
    if (!touch) return;

    const differenceX = this.#touchStartX - touch.screenX;
    const differenceY = this.#touchStartY - touch.screenY;

    if (Math.abs(differenceX) < 45 || Math.abs(differenceX) < Math.abs(differenceY)) return;

    if (differenceX > 0) {
      this.next();
    } else {
      this.previous();
    }
  };

  #handleMouseEnter = () => {
    this.#hoverPaused = true;
    this.#stopAutoplay();
  };

  #handleMouseLeave = () => {
    this.#hoverPaused = false;
    this.#startAutoplay();
  };

  #handleFocusIn = () => {
    this.#focusPaused = true;
    this.#stopAutoplay();
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    const nextTarget = event.relatedTarget;

    if (!(nextTarget instanceof Node) || !this.contains(nextTarget)) {
      this.#focusPaused = false;
      this.#startAutoplay();
    }
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stopAutoplay();
      this.#stopAllVideos();
      return;
    }

    this.#startAutoplay();

    const activeSlide = this.slides[this.#index];
    if (activeSlide) this.#playActiveVideo(activeSlide);
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const selectedSlide = event.target.closest('[data-slide]');
    if (!(selectedSlide instanceof HTMLElement) || !this.contains(selectedSlide)) return;

    const selectedIndex = this.slides.indexOf(selectedSlide);
    if (selectedIndex < 0) return;

    this.#editorPaused = true;
    this.#stopAutoplay();
    this.#show(selectedIndex, false);
  };

  /** @param {Event} event */
  #handleBlockDeselect = (event) => {
    if (!(event.target instanceof Element)) return;

    const selectedSlide = event.target.closest('[data-slide]');
    if (!(selectedSlide instanceof HTMLElement) || !this.contains(selectedSlide)) return;

    this.#editorPaused = false;
    this.#startAutoplay();
  };
}

if (!customElements.get('anthill-split-slideshow-component')) {
  customElements.define('anthill-split-slideshow-component', AnthillSplitSlideshowComponent);
}
